import { Integration } from '@/middleware/integration';
import { RootAction } from '@/actions/actiontypes';
import { hashChanger, logger, persister } from '@/middleware/middleware';
import { rootReducer } from '@/reducers/rootReducer';
import { VegobjekterResult } from '@/domain/vegobjekter/VegobjekterResult';
import { RootState } from '@/state/RootState';
import { VegobjekttypeCategoryState } from '@/state/VegobjekterState';
import { configureStore, isImmutableDefault } from '@reduxjs/toolkit';
import type { Store } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import {
    TypedUseSelectorHook,
    useDispatch as useReduxDispatch,
    useSelector as useReduxSelector,
} from 'react-redux';

export type Dispatch = ThunkDispatch<RootState, Integration, RootAction>;
type AppStore = Store<RootState, RootAction> & { dispatch: Dispatch };

// Dev-only immutable check: API results and categorized result state are created once and replaced
// through state builders, so they are treated as leaves instead of being traversed after every action.
export const isImmutableStateValue = (value: unknown): boolean =>
    isImmutableDefault(value) ||
    value instanceof VegobjekterResult ||
    value instanceof VegobjekttypeCategoryState;

export const initStore = (integration: Integration): AppStore =>
    configureStore({
        reducer: rootReducer,
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware({
                thunk: { extraArgument: integration },
                immutableCheck: {
                    isImmutable: isImmutableStateValue,
                },
                serializableCheck: false,
            }).concat(logger, hashChanger, persister),
        preloadedState: new RootState().withConfig(integration.config),
    }) as AppStore;

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;
export const useDispatch = () => useReduxDispatch<Dispatch>();
